/**
 * Audio-visualizer scenes — GLSL ES 1.0 fragment shaders over one contract.
 *
 * Every scene reads the drivers of AudioReactiveCore (public/js/audio-reactive/
 * core.js) through these uniforms, set by renderer.js:
 *
 *   u_time            seconds — decorative motion only (twinkle, drift)
 *   u_flow            integrated motion: advances 0.18/s in silence, ~1.7/s at
 *   u_flowBass          full energy; bass / high variants. MOTION THAT FOLLOWS
 *   u_flowHigh          THE MUSIC IS WRITTEN WITH THESE, never as u_time × level:
 *                       that product jumps the picture by (elapsed time × change)
 *                       on every hit — the "picture lives its own life" of 2026-09
 *   u_level           0..1 loudness against the recent loud parts (fast)
 *   u_glow            0..1 the same, slower: for full-frame brightness
 *   u_bass u_mid u_high   0..1 per-group levels (silence and noise read 0)
 *   u_bassS u_midS u_highS  the same, slower: for LARGE areas (a curtain,
 *                     a lava field) — a big area pumping on every hit is a strobe
 *   u_kick u_snare u_hat  0..1 decaying hit pulses (kick = low hit dominating the mids)
 *   u_beat u_beatPhase    beat pulse and 0..1 phase; both 0 unless the tempo is locked
 *   u_beatLock        0..1 how sure the tempo is
 *   u_presence        0..1 anything above the background at all
 *   u_intensity       0..1 slow energy of the section
 *   u_bright          0..1 spectral brightness (dark bass-heavy → bright hi-hats)
 *   u_flash           0..1 full-frame flash, at most one per 0.45 s (WCAG 2.3.1: <= 3/s)
 *   u_motion          1, or ~0.35 under prefers-reduced-motion
 *   u_spectrum        32×2 texture: row 0 band levels, row 1 peak hold (low → high)
 *   u_events[8]       recent hits: xy position 0..1, age s, strength; u_eventKind[8]:
 *                     0 kick, 1 snare, 2 hat, 3 beat — for things that are BORN on a hit
 *
 * Rule of thumb for a scene: silence is dim and slow but alive; the level
 * changes size/extent, bass the large shapes, highs the sparkle, hits spawn
 * events. Full-frame brightness moves only with u_glow and u_flash.
 *
 * ES5. Exposed as window.AVScenes = { lib, vert, list, post }.
 */
(function (root) {
    'use strict';

    var LIB = [
        'precision mediump float;',
        'uniform float u_time, u_alpha, u_flow, u_flowBass, u_flowHigh;',
        'uniform vec2 u_resolution;',
        'uniform float u_level, u_glow, u_bass, u_mid, u_high, u_bassS, u_midS, u_highS;',
        'uniform float u_kick, u_snare, u_hat, u_beat, u_beatPhase, u_beatLock;',
        'uniform float u_presence, u_intensity, u_bright, u_flash, u_motion;',
        'uniform sampler2D u_spectrum;',
        'uniform vec4 u_events[8];',
        'uniform float u_eventKind[8];',
        'float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
        'float noise(vec2 p){',
        '  vec2 i=floor(p),f=fract(p);',
        '  f=f*f*(3.0-2.0*f);',
        '  float a=hash(i),b=hash(i+vec2(1,0)),c=hash(i+vec2(0,1)),d=hash(i+vec2(1,1));',
        '  return mix(mix(a,b,f.x),mix(c,d,f.x),f.y);',
        '}',
        'float fbm(vec2 p){',
        '  float v=0.0,a=0.5;',
        '  for(int i=0;i<6;i++){v+=a*noise(p);p*=2.01;a*=0.49;}',
        '  return v;',
        '}',
        'vec2 domWarp(vec2 p,float s){',
        '  return p+s*vec2(fbm(p+vec2(1.7,9.2)),fbm(p+vec2(8.3,2.8)));',
        '}',
        /* band level at x in 0..1 (low → high), row 0 level, row 1 peak */
        'float spec(float x){return texture2D(u_spectrum,vec2(x,0.25)).r;}',
        'float specPeak(float x){return texture2D(u_spectrum,vec2(x,0.75)).r;}',
        ''
    ].join('\n');

    var VERT = 'attribute vec2 a_pos;\nvoid main(){gl_Position=vec4(a_pos,0.0,1.0);}';

    /* 1. Campfire — the level is the size of the fire, a kick throws it up,
       highs and hats throw sparks. */
    var CAMPFIRE = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  float lv=u_level, kk=u_kick*u_motion;',
        '  float width=3.4-0.7*lv;',
        '  float shape=exp(-pow((uv.x-0.5)*width*asp,2.0))*smoothstep(0.0,0.12,uv.y);',
        '  vec2 p=vec2(uv.x*3.5*asp, uv.y*5.5);',
        '  p.y-=u_flow*1.15;',
        '  p.x+=sin(uv.y*3.0+u_time*0.5)*0.2*(1.0+u_mid*0.5);',
        '  p=domWarp(p,0.25+u_mid*0.3);',
        '  float n1=fbm(p), n2=fbm(p*1.8+vec2(3.1,7.2)), n3=fbm(p*0.6+vec2(5.0,1.0));',
        /* how high the fire reaches: embers in silence, a blaze at full level, a jump on the kick */
        '  float reach=0.35+0.45*lv+0.25*kk;',
        '  float flame=mix(mix(n1,n2,0.35),n3,0.15)*shape*clamp(1.0-uv.y/reach*0.7,0.0,1.0);',
        '  flame=smoothstep(0.03,0.55,flame)*(0.45+0.55*u_glow+0.35*kk+0.2*u_beat*u_motion);',
        '  vec3 c=vec3(0.008,0.002,0.0);',
        '  c=mix(c,vec3(0.5,0.03,0.0),smoothstep(0.0,0.18,flame));',
        '  c=mix(c,vec3(0.9,0.2,0.0),smoothstep(0.12,0.38,flame));',
        '  c=mix(c,vec3(1.0,0.5,0.02),smoothstep(0.3,0.58,flame));',
        '  c=mix(c,mix(vec3(1.0,0.78,0.25),vec3(1.0,0.9,0.75),u_bright),smoothstep(0.5,0.78,flame));',
        '  c=mix(c,vec3(1.0,0.95,0.85),smoothstep(0.78,1.0,flame));',
        /* sparks: rise with the high flow, more of them on hats */
        /* sparks: round, on an aspect-correct grid (square cells were blocks in HD video) */
        '  vec2 eg=vec2(uv.x*asp,uv.y)*34.0+vec2(0.0,-u_flowHigh*1.6);',
        '  vec2 ec=floor(eg);',
        '  float ember=step(0.993-0.018*u_hat*u_motion-0.006*u_high,hash(ec))*smoothstep(0.32,0.05,length(fract(eg)-0.5-0.25*(vec2(hash(ec+3.1),hash(ec+7.7))-0.5)));',
        '  c+=ember*vec3(1.0,0.45,0.05)*shape*smoothstep(0.08,0.5,uv.y)*(0.2+1.4*u_high+1.2*u_hat*u_motion);',
        '  float glow=exp(-4.0*length(uv-vec2(0.5,0.06)))*(0.12+0.35*u_bass+0.3*kk);',
        '  c+=glow*vec3(0.45,0.09,0.0);',
        '  c+=exp(-6.0*length(uv-vec2(0.5,0.0)))*vec3(0.2,0.05,0.0);',
        '  c+=flame*flame*vec3(0.15,0.03,0.0)*0.5;',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 2. Aurora — bass / mid / high drive the three curtains, the level
       lifts them, highs make them shimmer. */
    var AURORA = [
        'float starField(vec2 uv,float scale,float density,float drift,float depth,vec2 jit,float t){',
        '  vec2 p=uv*scale+vec2(t*drift,0.0);',
        '  vec2 g=floor(p),f=fract(p)-0.5;',
        '  float h=hash(g+jit);',
        '  if(h<(1.0-density))return 0.0;',
        '  float sz=mix(0.020,0.090,(h-(1.0-density))/density)*mix(1.6,0.5,depth);',
        '  float d=length(f);',
        '  float core=smoothstep(sz,0.0,d);',
        '  float halo=mix(0.0,exp(-d*22.0)*0.45,1.0-depth);',
        '  float twk=0.5+0.5*sin(t*(1.5+h*3.0)+h*6.28);',
        '  return (core+halo)*twk;',
        '}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  vec2 auv=vec2(uv.x*asp,uv.y);',
        '  vec3 sky=mix(vec3(0.020,0.010,0.04),vec3(0.0,0.003,0.012),smoothstep(0.0,0.85,uv.y));',
        '  sky+=vec3(0.05,0.025,0.012)*smoothstep(0.18,0.0,uv.y);',
        '  vec3 c=sky;',
        '  float starH=smoothstep(0.05,0.6,uv.y);',
        '  c+=starField(auv,260.0,0.07,0.005,1.0,vec2(0.13,0.71),u_time)*vec3(0.55,0.62,0.85)*0.45*starH;',
        '  c+=starField(auv,160.0,0.045,0.012,0.65,vec2(0.51,0.27),u_time)*vec3(0.78,0.82,0.95)*0.65*starH;',
        '  c+=starField(auv*1.05+vec2(0.3,0.0),100.0,0.03,0.022,0.35,vec2(0.83,0.45),u_time)*vec3(0.92,0.93,1.0)*0.85*starH*(0.6+0.6*u_high);',
        '  c+=starField(auv*1.1+vec2(0.7,0.0),60.0,0.018,0.04,0.05,vec2(0.21,0.95),u_time)*vec3(1.0,0.97,0.95)*1.1*starH*(0.6+0.8*u_high+0.6*u_hat*u_motion);',
        /* curtains: their top rises with the (slow) level; the curtains are
           big, so they brighten with the slow bands and ripple with the fast */
        '  float top=0.62+0.26*u_glow+0.05*u_kick*u_motion;',
        '  float band=smoothstep(0.22,0.5,uv.y)*smoothstep(top,top-0.35,uv.y);',
        '  float vert=exp(-(uv.y-0.55)*(uv.y-0.55)*(6.0-3.0*u_glow));',
        '  vec2 p1=vec2(uv.x*3.5*asp+u_flow*0.12,uv.y*1.8);',
        '  float c1=pow(fbm(p1+vec2(0.0,u_bass*0.15)),1.4)*band;',
        '  float c2=pow(fbm(p1*1.5+vec2(u_flowBass*0.07,4.0)),1.6)*band;',
        '  float c3=pow(fbm(p1*0.9+vec2(u_flowHigh*0.11,8.0)),1.5)*band;',
        '  c+=vec3(0.05,0.8,0.30)*c1*(0.18+0.85*u_bassS+0.2*u_bass+0.25*u_beat*u_motion)*vert;',
        '  c+=vec3(0.35,0.05,0.70)*c2*(0.12+0.8*u_midS+0.15*u_mid)*vert;',
        '  c+=vec3(0.05,0.50,0.85)*c3*(0.10+0.8*u_highS+0.15*u_high)*vert;',
        '  float shimmer=noise(uv*30.0+vec2(u_flowHigh*0.8,0.0))*(u_high*0.7+u_hat*0.8*u_motion)*0.09*band;',
        '  c+=shimmer*vec3(0.8,1.0,0.9);',
        '  c+=exp(-uv.y*14.0)*vec3(0.04,0.06,0.10);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 3. Deep Ocean — bubbles rise with the bass flow and are born with the
       bass, rays open with the level, a snare flashes the rays. */
    var OCEAN = [
        'float bubbleLayer(vec2 uv,float scale,float density,float rise,float wob,float depth){',
        '  vec2 p=uv*scale;',
        '  p.y-=rise;',
        '  p.x+=sin(p.y*2.0+u_time*0.7)*wob;',
        '  vec2 g=floor(p),f=fract(p)-0.5;',
        '  float h=hash(g);',
        '  if(h<(1.0-density))return 0.0;',
        '  float r=0.18+(h-(1.0-density))/density*0.2;',
        '  float d=length(f);',
        '  float edge=mix(0.04,0.18,depth);',
        '  float core=smoothstep(r,r-edge,d);',
        '  float rim=smoothstep(r-edge*0.3,r-edge*0.5,d)*(1.0-core);',
        '  return core*0.6+rim*0.9;',
        '}',
        'float godRay(vec2 uv,float x0,float theta,float width){',
        '  float dx=(uv.x-x0)*cos(theta)+(uv.y-1.0)*sin(theta);',
        '  float beam=exp(-pow(dx/width,2.0));',
        '  float flick=0.6+0.4*fbm(vec2(uv.x*3.0+u_flow*0.15,uv.y*4.0-u_flow*0.2));',
        '  return beam*flick*smoothstep(0.0,0.7,uv.y);',
        '}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  vec2 auv=vec2(uv.x*asp,uv.y);',
        '  vec3 c=mix(vec3(0.0,0.012,0.07),vec3(0.01,0.10,0.28),smoothstep(0.0,0.55,uv.y));',
        '  c=mix(c,vec3(0.04,0.30,0.55),smoothstep(0.55,1.0,uv.y));',
        '  vec2 cp=auv*5.0+vec2(u_flow*0.15,u_flow*0.07);',
        '  float c1=fbm(cp), c2=fbm(cp*1.3+5.0+vec2(-u_flow*0.1,u_flow*0.05));',
        '  float caustic=pow(abs(sin(c1*6.28)*sin(c2*6.28)),0.5)*smoothstep(0.0,0.45,uv.y);',
        '  c+=caustic*vec3(0.06,0.30,0.55)*(0.18+0.7*u_mid);',
        '  float open=0.5+0.9*u_level;',
        '  float ray=godRay(auv,0.20*asp,-0.12,(0.045+0.03*u_bass)*open)*0.9;',
        '  ray+=godRay(auv,0.55*asp,0.10,(0.04+0.025*u_mid)*open)*0.8;',
        '  ray+=godRay(auv,0.85*asp,-0.05,0.04*open)*0.6;',
        '  ray+=godRay(auv,0.40*asp,0.18,(0.025+0.02*u_high)*open)*0.5;',
        '  c+=ray*vec3(0.30,0.65,0.95)*(0.3+0.6*u_glow+0.6*u_snare*u_motion);',
        '  float row=fbm(vec2(uv.x*4.0+u_time*0.04,7.3))*0.25+fbm(vec2(uv.x*9.0-u_time*0.03,3.1))*0.08;',
        '  c=mix(c,vec3(0.0,0.02,0.06),smoothstep(0.185+row,0.175+row,uv.y)*0.85);',
        '  float fb=u_flowBass;',
        '  c+=bubbleLayer(auv,16.0,0.03+0.05*u_bass,fb*0.12,0.02,1.0)*vec3(0.10,0.35,0.55)*0.28;',
        '  c+=bubbleLayer(auv*0.92+vec2(u_time*0.02,0.0),10.0,0.025+0.05*u_bass,fb*0.25,0.045,0.5)*vec3(0.20,0.55,0.85)*0.45*(0.6+0.6*u_mid);',
        '  c+=bubbleLayer(auv*0.85+vec2(-u_time*0.03,0.0),6.0,0.02+0.05*u_bass+0.04*u_kick*u_motion,fb*0.42,0.075,0.0)*vec3(0.55,0.90,1.0)*0.65*(0.6+0.7*u_high);',
        '  c+=fbm(uv*1.5+vec2(u_time*0.05,0.0))*0.07*vec3(0.03,0.12,0.22);',
        '  float bio=step(0.996-0.01*u_hat*u_motion,hash(floor(uv*60.0+vec2(0.0,u_flowHigh*0.4))));',
        '  c+=bio*vec3(0.10,0.95,0.65)*(0.15+0.8*u_high+0.6*u_hat*u_motion)*smoothstep(0.5,0.0,uv.y);',
        '  c*=1.0-smoothstep(0.7,1.4,length((uv-0.5)*vec2(asp*0.6,1.0)))*0.35;',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 4. Nebula — the core swells with the bass and flares on the kick, the
       dust layers are bass / mid / high. */
    var NEBULA = [
        'float nebStars(vec2 ndc,float scale,float density,float drift,float depth,float seed,float t){',
        '  vec2 p=ndc*scale+vec2(t*drift,t*drift*0.4);',
        '  vec2 g=floor(p),f=fract(p)-0.5;',
        '  float h=hash(g+vec2(seed,seed*1.7));',
        '  if(h<(1.0-density))return 0.0;',
        '  float sz=mix(0.025,0.10,(h-(1.0-density))/density)*mix(1.5,0.55,depth);',
        '  float d=length(f);',
        '  return (smoothstep(sz,0.0,d)+mix(0.0,exp(-d*20.0)*0.5,1.0-depth))*(0.4+0.6*sin(t*(1.2+h*2.5)+h*6.28));',
        '}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  float r=length(ndc);',
        /* the swirl turns with the flow; no atan here — its jump at +-pi was a
           visible seam across the left half of the frame */
        '  float ang=u_flow*0.12, ca=cos(ang), sa=sin(ang);',
        '  vec2 q=vec2(ndc.x*ca-ndc.y*sa,ndc.x*sa+ndc.y*ca);',
        '  vec2 sp=q*1.6+vec2(r*0.8,-r*0.5);',
        '  vec2 sp2=domWarp(sp*1.6,0.3+0.3*u_bassS+0.1*u_bass);',
        '  float n1=fbm(sp2+u_flow*0.06);',
        '  float n2=fbm(sp2*1.3+vec2(4.0,2.0)+u_flowBass*0.05);',
        '  float n3=fbm(sp2*0.8+vec2(8.0,5.0)+u_flowHigh*0.07);',
        '  float fade=exp(-r*r*(0.7-0.2*u_glow));',
        '  vec3 c=vec3(0.008,0.0,0.02);',
        '  c+=vec3(0.55,0.06,0.4)*n1*fade*(0.25+0.8*u_bassS);',
        '  c+=vec3(0.12,0.06,0.7)*n2*fade*(0.2+0.75*u_midS);',
        '  c+=vec3(0.75,0.2,0.06)*n3*fade*0.35*(0.25+0.8*u_highS);',
        /* the core breathes with the bass and flares on a kick, but stays a core */
        '  float core=exp(-r*r*(2.6-0.9*u_bassS-0.45*u_kick*u_motion));',
        '  c+=core*vec3(0.7,0.5,0.9)*(0.14+0.4*u_glow+0.3*u_kick*u_motion);',
        '  c+=pow(core,3.0)*vec3(1.0,0.9,1.0)*(0.08+0.2*u_glow);',
        '  c+=pow(n1*n2,0.7)*fade*vec3(0.35,0.15,0.55)*(0.2+0.35*u_midS);',
        '  c+=nebStars(ndc,140.0,0.06,0.008,1.0,11.3,u_time)*vec3(0.65,0.65,0.95)*0.5;',
        '  c+=nebStars(ndc,80.0,0.035,0.018,0.5,7.7,u_time)*vec3(0.9,0.88,1.0)*0.85*(0.6+0.5*u_mid);',
        '  c+=nebStars(ndc*1.05,45.0,0.02,0.03,0.0,3.1,u_time)*vec3(1.0,0.96,0.92)*1.2*(0.6+0.8*u_high+0.6*u_hat*u_motion);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 5. Lava — flows with the music, glows with the bass, cracks flare on kicks. */
    var LAVA = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  vec2 p=vec2(uv.x*asp,uv.y)*3.0; float t=u_flow*0.16;',
        '  p=domWarp(p,0.4+0.35*u_bass);',
        '  float n1=fbm(p+vec2(t,-t*0.7));',
        '  float n2=fbm(p*1.3+vec2(-t*0.5,t*0.8)+5.0);',
        '  float n3=fbm(p*0.5+vec2(t*0.2,t*0.3)+12.0);',
        '  float flow=fbm(vec2(uv.x*asp*2.0,uv.y*3.0-u_flow*0.1));',
        /* the whole frame is lava: it glows with the slow bands, the kick
           shows in the thin veins below, not as a full-frame pump */
        '  float lava=smoothstep(0.32,0.55,n1)*(0.45+0.7*u_bassS+0.1*u_bass);',
        '  float lava2=smoothstep(0.38,0.6,n2)*(0.35+0.6*u_midS);',
        '  float crack=pow(abs(n1-n2),0.35);',
        '  vec3 rock=vec3(0.04,0.02,0.01)+flow*0.04*vec3(0.1,0.04,0.01);',
        '  float heat=max(lava,lava2)*crack;',
        '  vec3 c=rock;',
        '  c=mix(c,vec3(0.8,0.12,0.0),smoothstep(0.04,0.2,heat));',
        '  c=mix(c,vec3(1.0,0.45,0.0),smoothstep(0.18,0.42,heat));',
        '  c=mix(c,vec3(1.0,0.8,0.3),smoothstep(0.42,0.7,heat));',
        '  c=mix(c,vec3(1.0,0.95,0.7),smoothstep(0.7,0.95,heat));',
        '  c+=smoothstep(0.08,0.4,heat)*0.18*(0.4+0.8*u_highS)*vec3(0.3,0.05,0.0);',
        '  float vein=pow(1.0-abs(n1-0.5)*2.0,14.0)*0.4*(0.25+0.6*u_glow+0.9*u_kick*u_motion);',
        '  c+=vein*vec3(0.7,0.1,0.0);',
        '  float smoke=fbm(uv*2.0+vec2(0.0,u_flow*0.18))*smoothstep(0.5,0.9,uv.y)*0.15;',
        '  c=mix(c,vec3(0.08,0.06,0.04),smoke*n3);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 6. Storm — a strike is a flash, so the big bolt and the sky go through
       u_flash (a strong kick / snare / beat, at most one per 0.45 s); snares
       and hats crackle in thin bolts that do not light the frame. */
    var STORM = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  vec2 auv=vec2(uv.x*asp,uv.y);',
        '  float cl1=fbm(auv*vec2(2.0,1.5)+u_flow*vec2(0.035,0.012));',
        '  float cl2=fbm(auv*vec2(3.0,2.0)+u_flow*vec2(-0.025,0.018)+5.0);',
        '  float cl3=fbm(auv*vec2(1.5,1.0)+u_flow*vec2(0.012,-0.012)+10.0);',
        '  float clouds=cl1*0.5+cl2*0.3+cl3*0.2;',
        '  vec3 sky=mix(vec3(0.012,0.012,0.04),vec3(0.06,0.04,0.14),clouds);',
        '  sky=mix(sky,vec3(0.09,0.06,0.18),smoothstep(0.3,0.8,uv.y)*clouds*(0.6+0.5*u_glow));',
        '  vec2 p1=domWarp(auv*vec2(3.0,5.0),0.25+u_highS*0.3);',
        '  float n1=fbm(p1+u_flowBass*vec2(0.12,0.06));',
        '  float bolt1=pow(1.0-abs(n1-0.5)*2.0,26.0-12.0*u_flash);',
        '  vec2 p2=domWarp(auv*vec2(4.0,6.0)+vec2(2.0,1.0),0.18+u_highS*0.25);',
        '  float n2=fbm(p2+u_flow*vec2(0.08,0.06)+3.0);',
        '  float bolt2=pow(1.0-abs(n2-0.5)*2.0,24.0);',
        '  float n3=fbm(auv*vec2(5.0,7.0)+vec2(4.0,3.0)+u_flowHigh*vec2(0.12,0.04)+7.0);',
        '  float bolt3=pow(1.0-abs(n3-0.5)*2.0,30.0);',
        '  float vf=smoothstep(0.0,0.25,uv.y)*smoothstep(1.0,0.35,uv.y);',
        '  float m=u_motion;',
        '  vec3 lt=vec3(0.4,0.55,1.0)*bolt1*(0.06+0.6*u_bassS+3.0*u_flash)*vf;',
        '  lt+=vec3(0.6,0.4,0.9)*bolt2*(0.04+0.55*u_midS+1.4*u_snare*m)*vf;',
        '  lt+=vec3(0.8,0.8,1.0)*bolt3*(0.03+0.5*u_highS+1.3*u_hat*m)*vf;',
        '  sky+=u_flash*0.3*vec3(0.25,0.25,0.45);',
        '  vec3 c=sky+lt;',
        '  c+=bolt1*exp(-4.0*length(uv-vec2(0.5,0.5)))*u_flash*0.5*vec3(0.2,0.3,0.5);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 7. Neon Pulse — rings are bass / mid / high: radius and glow; every hit
       sends a shockwave ring out from the centre. */
    var NEON = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  float r=length(ndc); float a=atan(ndc.y,ndc.x);',
        '  float m=u_motion;',
        '  float r1=0.22+0.16*u_bass+0.06*u_kick*m+sin(a*5.0+u_flow*1.2)*0.025*(1.0+u_mid);',
        '  float r2=0.46+0.1*u_mid+0.04*u_snare*m+sin(a*8.0-u_flowBass*1.0)*0.018*(1.0+u_high);',
        '  float r3=0.72+0.08*u_high+0.03*u_hat*m+sin(a*12.0+u_flowHigh*0.8)*0.012;',
        '  float r4=1.02+sin(a*3.0-u_flow*0.5)*0.015+u_beat*0.04*m;',
        '  vec3 c=vec3(0.008,0.0,0.015);',
        '  c+=vec3(0.08,0.45,1.0)*0.006/(abs(r-r1)+0.002)*(0.2+0.8*u_bass)*0.55;',
        '  c+=vec3(0.8,0.08,0.9)*0.005/(abs(r-r2)+0.002)*(0.2+0.8*u_mid)*0.45;',
        '  c+=vec3(1.0,0.2,0.35)*0.004/(abs(r-r3)+0.002)*(0.2+0.8*u_high)*0.4;',
        '  c+=vec3(0.1,0.9,0.5)*0.003/(abs(r-r4)+0.003)*(0.3+0.5*u_beatLock)*0.25;',
        /* shockwaves: one per recent hit, expanding from the centre */
        '  for(int i=0;i<8;i++){',
        '    vec4 e=u_events[i];',
        '    if(e.w<=0.0) continue;',
        '    float k=u_eventKind[i];',
        '    float speed=k<0.5?0.9:(k<1.5?1.2:1.6);',
        '    float rr=0.18+e.z*speed;',
        '    float fade=exp(-e.z*(k<0.5?2.2:(k<1.5?3.0:5.0)))*e.w*m;',
        '    vec3 col=k<0.5?vec3(0.2,0.55,1.0):(k<1.5?vec3(1.0,0.3,0.8):(k<2.5?vec3(1.0,0.9,0.6):vec3(0.3,1.0,0.6)));',
        '    c+=col*0.004/(abs(r-rr)+0.004)*fade*0.6;',
        '  }',
        '  float core=exp(-r*r*3.5)*(0.06+0.2*u_bass+0.2*u_kick*m);',
        '  c+=core*vec3(0.5,0.35,0.8);',
        '  float gr=step(0.988,fract(ndc.x*8.0))+step(0.988,fract(ndc.y*8.0));',
        '  c+=gr*0.015*vec3(0.15,0.2,0.3)*(0.3+0.5*u_glow+0.6*u_beat*m);',
        '  c*=smoothstep(2.0,0.4,r);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 8. Raindrops — every hit drops a ripple where it lands (kicks big,
       snares medium, hats small); the level is how hard it rains. */
    var RAIN = [
        'float ripple(vec2 uv,vec2 center,float age,float speed,float freq){',
        '  if(age<0.0||age>3.0)return 0.0;',
        '  float d=length(uv-center);',
        '  float r=age*speed;',
        '  float ring=sin((d-r)*freq)*exp(-age*1.4);',
        '  return ring*exp(-abs(d-r)*14.0)*smoothstep(3.0,0.3,age);',
        '}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  vec2 auv=vec2(uv.x*asp,uv.y);',
        '  float t=u_time;',
        '  vec3 water=mix(vec3(0.015,0.035,0.09),vec3(0.025,0.065,0.14),uv.y);',
        '  water+=noise(auv*8.0+vec2(u_flow*0.05,u_flow*0.025))*0.025*vec3(0.05,0.1,0.15);',
        '  float rip=0.0;',
        /* background drizzle: how many of 10 slots are active follows the level */
        '  for(int i=0;i<10;i++){',
        '    float fi=float(i);',
        '    if(hash(vec2(fi,7.7))>0.15+0.85*u_level) continue;',
        '    float interval=1.6+hash(vec2(fi,fi))*2.0;',
        '    float birth=floor(t/interval)*interval+hash(vec2(fi,1.5))*interval;',
        '    vec2 pos=vec2(hash(vec2(fi,floor(t/interval)))*asp,hash(vec2(floor(t/interval),fi)));',
        '    rip+=ripple(auv,pos,t-birth,0.16,40.0)*0.5;',
        '  }',
        /* drops born on the hits */
        '  for(int i=0;i<8;i++){',
        '    vec4 e=u_events[i];',
        '    if(e.w<=0.0) continue;',
        '    float k=u_eventKind[i];',
        '    float amp=k<0.5?1.3:(k<1.5?0.9:(k<2.5?0.45:0.7));',
        '    rip+=ripple(auv,vec2(e.x*asp,e.y),e.z,k<0.5?0.22:0.18,k<0.5?30.0:45.0)*amp*e.w*u_motion;',
        '  }',
        '  water+=rip*vec3(0.12,0.2,0.32)*(0.7+0.5*u_mid);',
        '  water+=noise(uv*25.0+vec2(u_flowHigh*0.3,0.2*t))*u_high*0.06*vec3(0.15,0.25,0.35);',
        '  water+=fbm(uv*6.0+u_flow*0.1)*0.05*(0.4+0.5*u_mid)*vec3(0.06,0.14,0.22);',
        '  water+=exp(-abs(uv.x-0.5)*3.0)*smoothstep(0.6,1.0,uv.y)*0.06*(0.6+0.6*u_glow)*vec3(0.1,0.15,0.25);',
        '  gl_FragColor=vec4(water,u_alpha);',
        '}'
    ].join('\n');

    /* 9. Galaxy — turns with the music (flow), arms are bass / mid / high,
       the core flares on the kick. */
    var GALAXY = [
        'float galStars(vec2 ndc,float scale,float density,float spin,float depth,float seed,float t){',
        '  float ang=t*spin, ca=cos(ang), sa=sin(ang);',
        '  vec2 p=vec2(ndc.x*ca-ndc.y*sa,ndc.x*sa+ndc.y*ca)*scale;',
        '  vec2 g=floor(p),f=fract(p)-0.5;',
        '  float h=hash(g+vec2(seed,seed*0.7+1.0));',
        '  if(h<(1.0-density))return 0.0;',
        '  float sz=mix(0.025,0.10,(h-(1.0-density))/density)*mix(1.5,0.55,depth);',
        '  float d=length(f);',
        '  return (smoothstep(sz,0.0,d)+mix(0.0,exp(-d*20.0)*0.5,1.0-depth))*(0.4+0.6*sin(u_time*(2.0+h*2.5)+h*6.28));',
        '}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  float r=length(ndc); float a=atan(ndc.y,ndc.x);',
        '  float spiral=a+r*4.0-u_flow*0.22;',
        '  float arm1=pow(0.5+0.5*sin(spiral*2.0),4.0)*exp(-r*(0.95-0.3*u_level));',
        '  float arm2=pow(0.5+0.5*sin(spiral*2.0+2.09),4.0)*exp(-r*0.9);',
        '  float arm3=pow(0.5+0.5*sin(spiral*2.0+4.19),4.0)*exp(-r*1.0);',
        '  float dust=fbm(vec2(spiral*0.3,r*3.0)+u_flow*0.03)*(arm1+arm2*0.7+arm3*0.5);',
        '  vec3 c=vec3(0.005,0.002,0.015);',
        '  c+=vec3(0.3,0.5,1.0)*arm1*dust*(0.3+1.0*u_bass);',
        '  c+=vec3(0.8,0.3,0.6)*arm2*dust*(0.25+0.9*u_mid);',
        '  c+=vec3(0.4,0.2,0.8)*arm3*dust*(0.2+0.8*u_high);',
        '  float core=exp(-r*r*(2.4-1.1*u_kick*u_motion))*(0.22+0.4*u_glow+0.3*u_kick*u_motion);',
        '  c+=core*vec3(1.0,0.9,0.7)*0.45;',
        '  c+=pow(core,4.0)*vec3(1.0,1.0,0.95)*0.3;',
        '  float armMask=max(arm1,max(arm2,arm3));',
        '  c+=galStars(ndc,160.0,0.06,0.012,1.0,17.3,u_flow*0.6)*vec3(0.7,0.78,1.0)*0.5*(0.6+armMask*0.5);',
        '  c+=galStars(ndc,95.0,0.04,0.022,0.5,5.1,u_flow*0.6)*vec3(0.95,0.95,1.0)*0.85*(0.5+0.5*u_high+armMask*0.5);',
        '  c+=galStars(ndc*1.05,55.0,0.022,0.04,0.0,9.7,u_flow*0.6)*vec3(1.0,0.97,0.9)*1.2*(0.5+0.8*u_high+0.6*u_hat*u_motion+armMask*0.4);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 10. Liquid Chrome — the metal ripples harder with the bass and the
       level, highlights catch the highs. */
    var CHROME = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  vec2 p=ndc*2.0;',
        '  float t=u_flow*0.24;',
        '  p=domWarp(p,0.35+0.55*u_bass+0.25*u_kick*u_motion);',
        '  float n1=fbm(p+vec2(t,t*0.7));',
        '  float n2=fbm(p*1.5+vec2(-t*0.6,t*0.4)+3.0);',
        '  float n3=fbm(p*0.8+vec2(t*0.3,-t*0.5)+7.0);',
        '  float metal=pow(n1,0.7)*0.5+pow(n2,0.8)*0.3+pow(n3,0.9)*0.2;',
        '  float edge=pow(abs(n1-n2),0.3);',
        '  vec3 c=mix(vec3(0.5,0.6,0.8),vec3(0.85,0.75,0.6),metal);',
        '  c=mix(c,vec3(1.0,0.98,0.95),smoothstep(0.65,0.85,metal)*(0.35+0.65*u_high));',
        '  c*=(0.2+edge*0.6)*(0.55+0.55*u_glow);',
        '  float spec=pow(max(0.0,1.0-abs(n1-0.7)*4.0),8.0);',
        '  c+=spec*vec3(1.0,0.95,0.9)*0.6*(0.25+0.6*u_mid+0.5*u_hat*u_motion);',
        '  float env=0.5+0.5*sin(atan(ndc.y,ndc.x)*3.0+n1*4.0+u_flow*0.3);',
        '  c*=(0.7+env*0.3);',
        '  c+=exp(-length(ndc)*1.5)*vec3(0.15,0.12,0.1)*(0.15+0.4*u_bass+0.3*u_kick*u_motion);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 11. Matrix Rain — columns fall with the flow (faster when loud), glyphs
       flicker with the highs, the trail grows with the level. */
    var MATRIX = [
        'float glyph(vec2 uv,float seed){',
        '  vec2 g=floor(uv*vec2(3.0,5.0));',
        '  float bit=step(0.45,hash(g+seed*17.0));',
        '  vec2 f=fract(uv*vec2(3.0,5.0));',
        '  return bit*step(0.15,f.x)*step(f.x,0.85)*step(0.1,f.y)*step(f.y,0.9);',
        '}',
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float cols=30.0; float rows=cols*u_resolution.y/u_resolution.x;',
        '  vec2 cell=vec2(floor(uv.x*cols),floor(uv.y*rows));',
        '  vec2 cuv=fract(vec2(uv.x*cols,uv.y*rows));',
        '  float colSpeed=0.5+hash(vec2(cell.x,0.0))*1.5;',
        '  float drop=fract(-u_flow*colSpeed*0.22+hash(vec2(cell.x,1.0)));',
        '  float dist=fract(uv.y-drop);',
        /* how many columns are raining follows the level: a few in silence, all when loud */
        '  float on=smoothstep(0.0,0.08,0.22+0.78*u_glow-hash(vec2(cell.x,3.3)));',
        '  float trail=smoothstep(0.0,0.2+0.4*u_glow,dist)*smoothstep(1.0,0.6-0.25*u_bassS,dist)*on;',
        '  float seed=floor(u_flowHigh*5.0+cell.x*7.0+cell.y*13.0);',
        '  float ch=glyph(cuv,hash(cell+seed));',
        '  float head=smoothstep(0.02,0.0,abs(dist-0.001))*on;',
        '  vec3 c=vec3(0.0,0.01,0.0);',
        '  c+=vec3(0.05,0.8,0.15)*trail*ch*0.7*(0.3+0.7*u_glow);',
        '  c+=vec3(0.6,1.0,0.7)*head*ch*(0.4+0.6*u_highS+0.8*u_hat*u_motion);',
        '  c+=vec3(0.02,0.15,0.04)*trail*0.15;',
        '  c+=exp(-abs(uv.x-cell.x/cols)*cols*0.8)*trail*0.03*(0.3+0.6*u_mid)*vec3(0.02,0.2,0.05);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 12. Particle Field — orbs orbit with the flow, swell with the bass;
       hits make them burst from their places. */
    var PARTICLES = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  vec3 c=vec3(0.005,0.008,0.02);',
        '  float t=u_flow;',
        '  vec2 sg=floor((ndc+2.0)*180.0);',
        '  c+=step(0.997,hash(sg))*(0.4+0.6*sin(u_time*1.8+hash(sg+0.7)*6.28))*vec3(0.55,0.62,0.85)*0.35;',
        '  c+=fbm(ndc*1.5+u_time*0.03)*0.04*vec3(0.05,0.08,0.15);',
        '  float burst=u_kick*u_motion;',
        '  for(int i=0;i<25;i++){',
        '    float fi=float(i);',
        '    float z=hash(vec2(fi,fi*1.7+0.3));',
        '    float near=1.0-z;',
        '    float speed=mix(0.55,1.4,near);',
        '    vec2 pos=vec2(sin(t*0.25*speed+fi*0.7+hash(vec2(fi,0.0))*6.28)*mix(0.6,1.4,near),',
        '                  cos(t*0.2*speed+fi*0.5+hash(vec2(0.0,fi))*6.28)*mix(0.45,0.95,near));',
        '    pos*=1.0+0.18*burst*near;',
        '    float sz=mix(0.010,0.070,near)*(0.7+0.6*u_bass)+0.02*burst*near;',
        '    float d=length(ndc-pos);',
        '    float glow=min(sz/(d*d+0.001),8.0);',
        '    float core=exp(-d*d/(sz*sz*mix(4.0,2.0,near)));',
        '    float hue=fract(fi*0.13+u_time*0.02+u_bright*0.3);',
        '    vec3 col;',
        '    if(hue<0.33) col=mix(vec3(0.2,0.5,1.0),vec3(0.1,0.9,0.8),hue*3.0);',
        '    else if(hue<0.66) col=mix(vec3(0.1,0.9,0.8),vec3(0.9,0.3,0.8),(hue-0.33)*3.0);',
        '    else col=mix(vec3(0.9,0.3,0.8),vec3(0.2,0.5,1.0),(hue-0.66)*3.0);',
        '    col=mix(col*vec3(0.65,0.78,1.0)*0.85,col,near);',
        '    c+=glow*col*0.008*(0.3+0.7*u_mid)*near;',
        '    c+=core*col*0.2*mix(0.35,1.0,near)*(0.35+0.8*u_glow+0.5*u_hat*u_motion);',
        '  }',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 13. Waveform — seven lines, each one a slice of the REAL spectrum:
       the bottom line is the lowest bands, the top one the highest. */
    var WAVEFORM = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  vec3 c=vec3(0.005,0.005,0.02);',
        '  for(int i=0;i<7;i++){',
        '    float fi=float(i);',
        '    float band=(fi+0.5)/7.0;',
        '    float a=spec(band);',
        '    float amp=0.02+0.42*a;',
        '    float freq=1.5+fi*0.8;',
        '    float ph=u_flow*(0.6+fi*0.18)+fi*1.2;',
        '    float y0=(fi-3.0)*0.12;',
        '    float wave=y0+sin(ndc.x*freq+ph)*amp+sin(ndc.x*freq*2.1+ph*0.7+fi)*amp*0.3;',
        '    float d=abs(ndc.y-wave);',
        '    float glow=min(0.008/(d+0.003),5.0);',
        '    float line=smoothstep(0.008,0.0,d);',
        '    float hue=fi/7.0;',
        '    vec3 col;',
        '    if(hue<0.33) col=mix(vec3(1.0,0.4,0.2),vec3(0.9,0.2,0.8),hue*3.0);',
        '    else if(hue<0.66) col=mix(vec3(0.9,0.2,0.8),vec3(0.0,0.9,0.9),(hue-0.33)*3.0);',
        '    else col=mix(vec3(0.0,0.9,0.9),vec3(0.1,0.4,1.0),(hue-0.66)*3.0);',
        '    c+=glow*col*0.04*(0.3+1.4*a);',
        '    c+=line*col*0.8*(0.4+1.0*a);',
        '  }',
        '  c*=max(0.0,1.0-length(ndc)*0.4);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 14. Kaleidoscope — zooms with the bass and punches on the kick, the
       pattern flows with the music, colour shifts with the brightness. */
    var KALEIDOSCOPE = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  vec2 ndc=uv*2.0-1.0; ndc.x*=u_resolution.x/u_resolution.y;',
        '  float r=length(ndc); float a=atan(ndc.y,ndc.x);',
        '  a=mod(a,6.2832/6.0); a=abs(a-3.14159/6.0);',
        '  vec2 kp=vec2(cos(a),sin(a))*r;',
        '  kp*=1.7+0.7*u_bass+0.35*u_kick*u_motion;',
        '  kp=domWarp(kp+vec2(u_flow*0.1,u_flow*0.075),0.35+0.4*u_mid);',
        '  float n1=fbm(kp);',
        '  float n2=fbm(kp*1.5+vec2(3.0,5.0)+u_flowHigh*0.06);',
        '  float n3=fbm(kp*0.7+vec2(7.0,2.0)+u_flowBass*0.04);',
        '  float pattern=n1*0.5+n2*0.3+n3*0.2;',
        '  vec3 c=vec3(0.01,0.005,0.02);',
        '  float hue=fract(pattern+u_flow*0.025+u_bright*0.25);',
        '  vec3 col;',
        '  if(hue<0.25) col=mix(vec3(0.9,0.1,0.3),vec3(0.8,0.5,0.0),hue*4.0);',
        '  else if(hue<0.5) col=mix(vec3(0.8,0.5,0.0),vec3(0.1,0.8,0.4),(hue-0.25)*4.0);',
        '  else if(hue<0.75) col=mix(vec3(0.1,0.8,0.4),vec3(0.2,0.3,1.0),(hue-0.5)*4.0);',
        '  else col=mix(vec3(0.2,0.3,1.0),vec3(0.9,0.1,0.3),(hue-0.75)*4.0);',
        '  c+=col*pattern*(0.3+0.8*u_glow);',
        '  c+=pow(n1*n2,0.5)*exp(-r*0.5)*vec3(0.9,0.85,1.0)*0.3*(0.2+0.8*u_high+0.4*u_hat*u_motion);',
        '  c+=exp(-r*r*2.0)*(0.08+0.25*u_bass+0.25*u_kick*u_motion)*vec3(1.0,0.9,0.95);',
        '  c+=pow(abs(n1-0.5)*2.0,3.0)*0.3*col*0.2*(0.3+0.7*u_mid);',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    /* 15. Spectrum — the classic analyser: 48 bars a side from the 32 bands
       (low in the middle, mirrored), peak caps, a reflection, the beat in the floor. */
    var SPECTRUM = [
        'void main(){',
        '  vec2 uv=gl_FragCoord.xy/u_resolution;',
        '  float asp=u_resolution.x/u_resolution.y;',
        '  vec3 c=mix(vec3(0.01,0.01,0.03),vec3(0.02,0.015,0.05),uv.y);',
        '  float base=0.28;',
        '  float x=abs(uv.x-0.5)*2.0;',
        '  float bars=48.0;',
        '  float bi=floor(x*bars);',
        '  float fx=(bi+0.5)/bars;',
        '  float inBar=smoothstep(0.08,0.14,fract(x*bars))*smoothstep(0.92,0.86,fract(x*bars));',
        '  float lv=spec(fx);',
        '  float pk=specPeak(fx);',
        '  float h=0.015+0.6*lv;',
        '  float ph=0.015+0.6*pk;',
        '  vec3 col=mix(vec3(1.0,0.25,0.35),vec3(0.3,0.6,1.0),fx);',
        '  col=mix(col,vec3(0.3,1.0,0.8),smoothstep(0.6,1.0,fx));',
        '  float y=uv.y-base;',
        '  float bar=step(0.0,y)*step(y,h)*inBar;',
        '  c+=bar*col*(0.35+0.75*y/max(h,0.001));',
        '  c+=inBar*smoothstep(0.012,0.0,abs(y-ph-0.01))*col*0.9;',
        '  float refl=step(y,0.0)*step(-h*0.45,y)*inBar;',
        '  c+=refl*col*0.18*(1.0+y/max(h*0.45,0.001));',
        '  c+=exp(-abs(y-h)*40.0)*step(0.0,y)*inBar*col*0.15;',
        '  float floorGlow=exp(-abs(y)*30.0)*(0.05+0.25*u_beat*u_motion+0.12*u_kick*u_motion);',
        '  c+=floorGlow*vec3(0.5,0.4,1.0);',
        '  c*=0.85+0.15*u_glow;',
        '  gl_FragColor=vec4(c,u_alpha);',
        '}'
    ].join('\n');

    var POST = {
        blit: [
            'precision mediump float;',
            'uniform sampler2D u_tex;',
            'uniform vec2 u_res;',
            'void main(){gl_FragColor=texture2D(u_tex,gl_FragCoord.xy/u_res);}'
        ].join('\n'),
        bright: [
            'precision mediump float;',
            'uniform sampler2D u_tex;',
            'uniform vec2 u_res;',
            'uniform float u_thresh;',
            'void main(){',
            '  vec3 c=texture2D(u_tex,gl_FragCoord.xy/u_res).rgb;',
            '  float br=max(c.r,max(c.g,c.b));',
            '  gl_FragColor=vec4(c*smoothstep(u_thresh,u_thresh+0.25,br),1.0);',
            '}'
        ].join('\n'),
        blur: [
            'precision mediump float;',
            'uniform sampler2D u_tex;',
            'uniform vec2 u_res;',
            'uniform vec2 u_dir;',
            'void main(){',
            '  vec2 uv=gl_FragCoord.xy/u_res;',
            '  vec3 c=texture2D(u_tex,uv-u_dir*4.0).rgb*0.05+texture2D(u_tex,uv-u_dir*3.0).rgb*0.09;',
            '  c+=texture2D(u_tex,uv-u_dir*2.0).rgb*0.12+texture2D(u_tex,uv-u_dir).rgb*0.15;',
            '  c+=texture2D(u_tex,uv).rgb*0.18;',
            '  c+=texture2D(u_tex,uv+u_dir).rgb*0.15+texture2D(u_tex,uv+u_dir*2.0).rgb*0.12;',
            '  c+=texture2D(u_tex,uv+u_dir*3.0).rgb*0.09+texture2D(u_tex,uv+u_dir*4.0).rgb*0.05;',
            '  gl_FragColor=vec4(c,1.0);',
            '}'
        ].join('\n'),
        /* scene + bloom, radial chromatic aberration (highs), vignette, grain,
           tone map. Bloom swells with the (rate-limited) flash, never per hit. */
        composite: [
            'precision mediump float;',
            'uniform sampler2D u_scene, u_bloom;',
            'uniform vec2 u_res;',
            'uniform float u_time, u_bloom_intensity, u_ca_strength, u_grain, u_vignette, u_flash;',
            'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
            'void main(){',
            '  vec2 uv=gl_FragCoord.xy/u_res;',
            '  vec2 ndc=uv*2.0-1.0;',
            '  float r=length(ndc);',
            '  vec2 ca=ndc*r*0.0035*u_ca_strength;',
            '  vec3 scene=vec3(texture2D(u_scene,uv-ca).r,texture2D(u_scene,uv).g,texture2D(u_scene,uv+ca).b);',
            '  vec3 c=scene+texture2D(u_bloom,uv).rgb*u_bloom_intensity*(1.0+u_flash*0.6);',
            '  c*=1.0-smoothstep(0.6,1.4,r)*u_vignette;',
            '  c+=(h(uv*u_res*0.5+u_time*0.7)-0.5)*u_grain;',
            '  c=c/(1.0+c);',
            '  gl_FragColor=vec4(pow(c,vec3(0.95)),1.0);',
            '}'
        ].join('\n')
    };

    root.AVScenes = {
        lib: LIB,
        vert: VERT,
        post: POST,
        list: [
            { id: 'campfire',     frag: CAMPFIRE,     icon: '🔥', nameKey: 'scene_campfire' },
            { id: 'aurora',       frag: AURORA,       icon: '🌌', nameKey: 'scene_aurora' },
            { id: 'ocean',        frag: OCEAN,        icon: '🌊', nameKey: 'scene_ocean' },
            { id: 'nebula',       frag: NEBULA,       icon: '✨',       nameKey: 'scene_nebula' },
            { id: 'lava',         frag: LAVA,         icon: '🌋', nameKey: 'scene_lava' },
            { id: 'storm',        frag: STORM,        icon: '⚡',       nameKey: 'scene_storm' },
            { id: 'neon',         frag: NEON,         icon: '💜', nameKey: 'scene_neon' },
            { id: 'rain',         frag: RAIN,         icon: '🌧️', nameKey: 'scene_rain' },
            { id: 'galaxy',       frag: GALAXY,       icon: '🌀', nameKey: 'scene_galaxy' },
            { id: 'chrome',       frag: CHROME,       icon: '🪩', nameKey: 'scene_chrome' },
            { id: 'matrix',       frag: MATRIX,       icon: '🟩', nameKey: 'scene_matrix' },
            { id: 'particles',    frag: PARTICLES,    icon: '🫧', nameKey: 'scene_particles' },
            { id: 'waveform',     frag: WAVEFORM,     icon: '〰️', nameKey: 'scene_waveform' },
            { id: 'kaleidoscope', frag: KALEIDOSCOPE, icon: '🔮', nameKey: 'scene_kaleidoscope' },
            { id: 'spectrum',     frag: SPECTRUM,     icon: '🎚️', nameKey: 'scene_spectrum' }
        ]
    };
})(typeof window !== 'undefined' ? window : this);
